import { useState } from 'react';

import { useFormik } from 'formik';
import { mutate } from 'swr';
import * as Yup from 'yup';

import {
  FontSize,
  FontWeight,
  STATUSES,
  TEST_IDS,
  deliveryModes,
} from 'commons';
import { Button, Input, Select, Text } from 'components';
import { formValidations, messages, swrKeys } from 'helpers';
import { useAuth } from 'hooks';
import { consignmentService } from 'services';
import { ConsignmentDetails } from 'types';

import * as S from '../../styles';

interface Props {
  consignmentDetails: ConsignmentDetails;
  action: string;
}

const {
  READY_FOR_PICKUP_BY_DRIVER,
  READY_FOR_CUSTOMER_PICKUP_IN_STORE,
  CLARIFY_DELIVERY_FAIL,
  COLLECT_FAIL,
} = STATUSES;

export function CarrierForm({ consignmentDetails, action }: Props) {
  const { isSuperExpress, scheduledCollect } = consignmentDetails;
  const [isLoading, setIsLoading] = useState(false);
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;
  const { name: store } = selectedStore;

  const hasDeliveryOrCollectFailure =
    consignmentDetails.status === CLARIFY_DELIVERY_FAIL.value ||
    consignmentDetails.status === COLLECT_FAIL.value;

  const isAvailableToCollet: boolean = (() => {
    return [
      READY_FOR_PICKUP_BY_DRIVER.value,
      READY_FOR_CUSTOMER_PICKUP_IN_STORE.value,
      CLARIFY_DELIVERY_FAIL.value,
      COLLECT_FAIL.value,
    ].some(status => consignmentDetails.status === status);
  })();

  const carrierValidation = Yup.object().shape({
    name: Yup.string().required('Nome'),
    cpf: Yup.string().required('CPF'),
    deliveryMode: Yup.string().required('Transportadora'),
  });

  const uberDeliveryMode = deliveryModes.find(mode => mode.label === 'Uber');

  const formCollect = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: scheduledCollect?.driverName || '',
      cpf: '',
      deliveryMode: isSuperExpress ? uberDeliveryMode?.value || '' : '',
      pickUpAuthCode: '',
    },

    validateOnBlur: false,

    validateOnChange: false,

    validationSchema: carrierValidation,

    validate: values => {
      formValidations.validationSchema({
        schema: carrierValidation,
        values,
      });
    },

    onSubmit: async values => {
      setIsLoading(true);

      if (!store) {
        return messages([{ errorCode: 'FRONT-RE-STORE-NOT-FOUND' }]);
      }

      const response = await consignmentService.postCollect({
        consignmentCode: consignmentDetails.code,
        store,
        action,
        name: values.name,
        cpf: values.cpf,
        deliveryMode: values.deliveryMode,
        pickUpAuthCode: null,
      });

      if (response?.status === 200) {
        messages([
          { errorCode: 'FRONT-RE-CONSIGNMENT_COLLECTED' },
          { subject: consignmentDetails.code, subjectType: 'consignmentCode' },
        ]);

        if (hasDeliveryOrCollectFailure) {
          mutate(
            swrKeys.consignmentDetails({
              consignmentCode: consignmentDetails.code,
            })
          );
        }

        mutate(swrKeys.consignments({ key: store }));
      } else {
        setIsLoading(false);
      }
    },
  });

  return (
    <S.Form onSubmit={formCollect.handleSubmit}>
      <S.Wrapper>
        <S.InputWrapper>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Nome do Transportador
          </Text>

          <Input
            name="name"
            placeholder="Digite o nome completo"
            onChange={formCollect.handleChange}
            value={formCollect.values.name}
            status={formCollect.errors.name ? 'error' : 'default'}
            disabled={isAdmin}
          />
        </S.InputWrapper>

        <S.InputWrapper $small>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            CPF do Transportador
          </Text>

          <Input
            name="cpf"
            placeholder="xxx.xxx.xxx-xx"
            type="tel"
            onChange={formCollect.handleChange}
            value={formCollect.values.cpf}
            mask="999.999.999-99"
            status={formCollect.errors.cpf ? 'error' : 'default'}
            disabled={isAdmin}
          />
        </S.InputWrapper>
      </S.Wrapper>

      <S.Wrapper>
        <S.InputWrapper $small>
          <Text size={FontSize.font12} weight={FontWeight.bold}>
            Transportadora
          </Text>

          <Select.Wrapper
            data-testid={TEST_IDS.DELIVERY_MODE_SELECT}
            onChange={formCollect.handleChange}
            name="deliveryMode"
            options={deliveryModes}
            status={formCollect.errors.name ? 'error' : 'default'}
            disabled={isAdmin || isSuperExpress}
            initialSelected={isSuperExpress ? uberDeliveryMode : undefined}
          >
            <Select.Input placeholder="Selecione..." />

            <Select.StatusArrow />
          </Select.Wrapper>
        </S.InputWrapper>

        <S.ButtonWrapper>
          {isAvailableToCollet ? (
            <Button
              data-testid={TEST_IDS.CONFIRM_COLLECT_BUTTON}
              full
              type="submit"
              disabled={isLoading || isAdmin}
            >
              {isLoading ? 'Enviando...' : 'Registrar Retirada'}
            </Button>
          ) : (
            <Text size={FontSize.font12}>
              Aguarde o pedido ficar disponível para confirmar a coleta
            </Text>
          )}
        </S.ButtonWrapper>
      </S.Wrapper>
    </S.Form>
  );
}
